import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import KanbanBoard from "../core/KanbanBoard";
import { useAppData } from "../../context/AppDataContext";

import TaskAssignments from "./TaskAssignments";

const TaskKanban = ({
  tasks,
  loading,
  onStatusChange,
  onTaskClick,
  taskStatuses: providedStatuses,
}) => {
  const history = useHistory();
  const { api } = useAppData();
  const [taskStatuses, setTaskStatuses] = useState(providedStatuses || []);
  const hasProvidedStatuses = Array.isArray(providedStatuses);
  const [statusesLoading, setStatusesLoading] = useState(!hasProvidedStatuses);

  useEffect(() => {
    if (hasProvidedStatuses) {
      setTaskStatuses(providedStatuses || []);
      setStatusesLoading(false);
      return;
    }

    const fetchTaskStatuses = async () => {
      try {
        setStatusesLoading(true);
        const statusesData = await api.taskStatus.getTaskStatuses();
        setTaskStatuses(statusesData);
      } catch (error) {
        console.error("Error fetching task statuses:", error);
        setTaskStatuses([]);
      } finally {
        setStatusesLoading(false);
      }
    };

    fetchTaskStatuses();
  }, [api, hasProvidedStatuses, providedStatuses]);

  const getStatusColumns = () => {
    return taskStatuses.map((status) => ({
      id: status.name.toLowerCase().replace(/\s+/g, "_"),
      title: status.name,
      color: status.color,
      task_status_id: status.task_status_id,
    }));
  };

  const transformTasks = () => {
    const columns = {};
    const statusColumns = getStatusColumns();

    statusColumns.forEach((column) => {
      columns[column.id] = {
        id: column.id,
        title: column.title,
        color: column.color,
        items: tasks
          .filter((task) => {
            const statusName =
              typeof task.status === "string" ? task.status : task.status?.name;

            if (!statusName) return false;

            return statusName.toLowerCase().replace(/\s+/g, "_") === column.id;
          })
          .map((task) => ({
            id: task.task_id,
            content: (
              <div
                className="task-card"
                onClick={() => {
                  if (onTaskClick) {
                    onTaskClick(task.task_id);
                  } else {
                    history.push(`/task/${task.task_id}`);
                  }
                }}
              >
                <span>Task: {task.name}</span>
                <div>
                  <span className="project-name">
                    Project: {task.project?.name}
                  </span>
                </div>

                <div className="task-card-details">
                  <span className={`priority priority-${task.priority}`}>
                    {task.priority?.charAt(0).toUpperCase() +
                      task.priority?.slice(1)}
                  </span>
                  {task.points_estimate && (
                    <span className="points">{task.points_estimate} pts</span>
                  )}
                </div>
                {task.assignments?.length ? (
                  <TaskAssignments
                    taskId={task.task_id}
                    task={task}
                    assignments={task.assignments || []}
                    readOnly={true}
                  />
                ) : (
                  <span>No Assignee</span>
                )}
              </div>
            ),
          })),
      };
    });
    return columns;
  };

  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId) {
      return;
    }

    const statusColumns = getStatusColumns();
    const targetStatus = statusColumns.find(
      (status) => status.id === destination.droppableId
    );

    if (targetStatus) {
      onStatusChange(draggableId, targetStatus.task_status_id);
    }
  };

  if (loading || statusesLoading) {
    return <div className="loading">Loading tasks...</div>;
  }

  return (
    <div
      className="task-kanban"
      style={{
        minWidth: "max-content",
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <KanbanBoard columns={transformTasks()} onDragEnd={handleDragEnd} />
    </div>
  );
};

TaskKanban.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      task_id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          name: PropTypes.string,
        }),
      ]),
      priority: PropTypes.string,
      points_estimate: PropTypes.number,
      assigned_users: PropTypes.arrayOf(
        PropTypes.shape({
          user_id: PropTypes.string.isRequired,
          first_name: PropTypes.string,
          last_name: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  loading: PropTypes.bool,
  onStatusChange: PropTypes.func.isRequired,
  onTaskClick: PropTypes.func,
};

export default TaskKanban;
